import { buildConfig, readDataAttributes, validateConfig } from './config';
import { telemetry } from './telemetry';
import { VERSION, CHANNEL, BUILD_DATE, getVersionInfo } from '../version';
import { initializeMonitoring, reportVersionUsage } from '../monitoring';
import { WidgetClient } from './client';
import { getOrCreateSessionId } from './storage';
import type { WidgetConfig } from './types';

const IFRAME_ID = 'fluxbot-secure-widget-frame';

/**
 * Build the embed URL for the widget iframe.
 *
 * In session-flow mode (default when createSession succeeds), the token is NOT
 * included in the URL — the iframe authenticates via the secure cookie set by
 * POST /api/v1/widget/session.
 *
 * Falls back to token-in-URL (legacy flow) only when session creation fails.
 */
function buildEmbedUrl(config: WidgetConfig, options?: { useSessionFlow?: boolean; sessionId?: string }): string {
  const endpoint = config.endpoint.replace(/\/$/, '');
  const params = new URLSearchParams({
    parentOrigin: window.location.origin,
    locale: config.locale,
    position: config.position,
    primaryColor: config.primaryColor,
    greeting: config.greeting,
  });

  // Session flow: no token in URL, cookie handles auth
  if (options?.useSessionFlow && options?.sessionId) {
    params.set('sessionId', options.sessionId);
    return `${endpoint}/api/${config.apiVersion}/widget/embed?${params.toString()}`;
  }

  // Legacy flow: token in URL (fallback)
  params.set('token', config.token);
  return `${endpoint}/api/${config.apiVersion}/widget/embed?${params.toString()}`;
}

function getFrameStyles(position: WidgetConfig['position']): Partial<CSSStyleDeclaration> {
  const styles: Partial<CSSStyleDeclaration> = {
    position: 'fixed',
    width: 'min(420px, 100vw)',
    height: 'min(680px, 100vh)',
    border: '0',
    background: 'transparent',
    zIndex: '2147483647',
    colorScheme: 'normal',
  };

  if (position.includes('top')) {
    styles.top = '0';
  } else {
    styles.bottom = '0';
  }

  if (position.includes('left')) {
    styles.left = '0';
  } else {
    styles.right = '0';
  }

  return styles;
}

function applyStyles(element: HTMLElement, styles: Partial<CSSStyleDeclaration>): void {
  Object.entries(styles).forEach(([property, value]) => {
    if (typeof value === 'string' && value.length > 0) {
      element.style.setProperty(property.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`), value);
    }
  });
}

class FluxbotWidget {
  private iframe: HTMLIFrameElement | null = null;

  initialize(): void {
    try {
      initializeMonitoring();

      if (typeof console !== 'undefined' && console.log) {
        console.log(`[FluxBot Widget] v${VERSION} [${CHANNEL}] loaded successfully`);
      }

      const dataAttrs = readDataAttributes();
      const config = buildConfig(dataAttrs);

      if (!validateConfig(config)) {
        throw new Error('Invalid widget configuration');
      }

      // Attempt session flow (POST /session → cookie → iframe without token)
      this.mountFrameWithSession(config);

      telemetry.info(undefined, 'Secure widget frame mounted', {
        version: VERSION,
        channel: CHANNEL,
        domain: window.location.hostname,
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      telemetry.error(undefined, 'Widget initialization failed', {
        error: errorMsg,
      });
      console.error('Fluxbot Widget initialization failed:', error);
    }
  }

  /**
   * Attempt to create a session and mount the iframe using cookie-based auth.
   * Falls back to legacy token-in-URL flow if session creation fails.
   */
  private async mountFrameWithSession(config: WidgetConfig): Promise<void> {
    const client = new WidgetClient(config);
    let useSessionFlow = false;
    let sessionId: string | undefined;

    try {
      const session = await client.createSession();
      sessionId = session.sessionId;
      useSessionFlow = true;
      console.info('[FluxBot Widget] Session created, using secure cookie flow', {
        sessionId: session.sessionId,
        expiresAt: session.expiresAt,
      });
    } catch (error) {
      // Session creation failed — fall back to legacy token-in-URL flow
      console.warn('[FluxBot Widget] Session creation failed, falling back to token flow', {
        error: error instanceof Error ? error.message : String(error),
      });
    }

    this.mountFrame(config, { useSessionFlow, sessionId });
  }

  private mountFrame(
    config: WidgetConfig,
    options?: { useSessionFlow?: boolean; sessionId?: string },
  ): void {
    const existing = document.getElementById(IFRAME_ID);
    if (existing) {
      existing.remove();
    }

    const iframe = document.createElement('iframe');
    iframe.id = IFRAME_ID;
    iframe.title = 'FluxBot chat';
    iframe.src = buildEmbedUrl(config, options);
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.sandbox.add('allow-scripts', 'allow-forms', 'allow-same-origin');
    iframe.setAttribute('aria-live', 'polite');
    iframe.setAttribute('data-fluxbot-version', VERSION);
    if (options?.useSessionFlow) {
      iframe.setAttribute('data-auth-mode', 'session');
    }
    applyStyles(iframe, getFrameStyles(config.position));

    document.body.appendChild(iframe);
    this.iframe = iframe;
  }

  destroy(): void {
    this.iframe?.remove();
    this.iframe = null;
  }
}

declare global {
  interface Window {
    FluxbotWidget: typeof FluxbotWidget & {
      VERSION: string;
      CHANNEL: string;
      BUILD_DATE: string;
      getVersionInfo: typeof getVersionInfo;
      reportVersionUsage: typeof reportVersionUsage;
    };
  }
}

const widgetNamespace: any = FluxbotWidget;
widgetNamespace.VERSION = VERSION;
widgetNamespace.CHANNEL = CHANNEL;
widgetNamespace.BUILD_DATE = BUILD_DATE;
widgetNamespace.getVersionInfo = getVersionInfo;
widgetNamespace.reportVersionUsage = reportVersionUsage;
widgetNamespace.WidgetClient = WidgetClient;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const widget = new FluxbotWidget();
    widget.initialize();
  });
} else {
  const widget = new FluxbotWidget();
  widget.initialize();
}

export { FluxbotWidget, WidgetClient, VERSION, CHANNEL, BUILD_DATE, getVersionInfo };
